import React, { useEffect, useState } from "react";
import { Box, Text, Heading, Grid, GridItem } from "@chakra-ui/react";
import { useAppContext } from "../../../context/AppContext";
import { fetchQuickLinkGroups } from "../../../services/AdminServices/QuickLinkGroups";
import { fetchQuickLinks } from "../../../services/AdminServices/QuickLinks";
import { fetchUsersGroups } from "../../../services/AdminServices/UserGroups";
import { fetchUsers } from "../../../services/AdminServices/Users";
import { useNavigate } from "react-router-dom"; // Import useNavigate for navigation
import { useMsal } from "@azure/msal-react"; // Import useMsal for logout functionality
import { useToast } from "@chakra-ui/react"; // Import useToast for toast notifications
import "./Dashboard.css"; // Import the custom CSS file

const Dashboard: React.FC = () => {
  const [userCount, setUserCount] = useState<number>(0);
  const [activeUserCount, setActiveUserCount] = useState<number>(0);
  const [userGroupCount, setUserGroupCount] = useState<number>(0);
  const [activeUserGroupCount, setActiveUserGroupCount] = useState<number>(0);
  const [quickLinksCount, setQuickLinksCount] = useState<number>(0);
  const [activeQuickLinksCount, setActiveQuickLinksCount] = useState<number>(0);
  const [quickLinkGroupsCount, setQuickLinkGroupsCount] = useState<number>(0);
  const [activeQuickLinkGroupsCount, setActiveQuickLinkGroupsCount] =
    useState<number>(0);
  const { accessToken , userData} = useAppContext();
  const { accounts } = useMsal(); // MSAL instance and accounts
  const name = accounts && accounts.length > 0 ? accounts[0].name : "";

  const navigate = useNavigate(); // Initialize useNavigate hook for navigation
  const toast = useToast(); // Initialize useToast hook

  useEffect(() => {
    const getCounts = async () => {
      try {
        if (accessToken) {
          // Show loading toast
          toast({
            position: "top",
            title: "Loading data...",
            status: "info",
            duration: 3000,
            isClosable: true,
          });
          var TenantID=userData?.tenantID || 0;
          // Fetch data for all services
          const users = await fetchUsers(accessToken,TenantID);
          setUserCount(users.length);
          setActiveUserCount(users.filter((user: any) => user.isActive).length); // Filter active users

          const userGroups = await fetchUsersGroups(accessToken, TenantID);
          setUserGroupCount(userGroups.length);
          setActiveUserGroupCount(
            userGroups.filter((group: any) => group.isActive).length
          ); // Filter active user groups

          const quickLinks = await fetchQuickLinks(accessToken, TenantID);
          setQuickLinksCount(quickLinks.length);
          setActiveQuickLinksCount(
            quickLinks.filter((link: any) => link.isActive).length
          ); // Filter active quick links

          const quickLinkGroups = await fetchQuickLinkGroups(accessToken, TenantID);
          setQuickLinkGroupsCount(quickLinkGroups.length);
          setActiveQuickLinkGroupsCount(
            quickLinkGroups.filter((group: any) => group.isActive).length
          ); // Filter active quick link groups

          // Hide loading toast after data is fetched
          toast.closeAll();
        } else {
          toast({
            position: "top",
            title: "Error",
            description: "Access token is missing.",
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      } catch (error) {
        toast({
          position: "top",
          title: "Error",
          description: "Failed to fetch data.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    getCounts();
  }, [accessToken, toast]);

  // Mock data to display counts on the cards, including active counts
  const mockData = [
    {
      title: "No of Users",
      value: userCount.toString(),
      activeValue: activeUserCount.toString(),
      bgColor: "blue",
      route: "/users",
    },
    {
      title: "No of User Groups",
      value: userGroupCount.toString(),
      activeValue: activeUserGroupCount.toString(),
      bgColor: "green",
      route: "/usergroups",
    },
    {
      title: "No of Quick Links",
      value: quickLinksCount.toString(),
      activeValue: activeQuickLinksCount.toString(),
      bgColor: "purple",
      route: "/quicklinks",
    },
    {
      title: "No of Quick Link Groups",
      value: quickLinkGroupsCount.toString(),
      activeValue: activeQuickLinkGroupsCount.toString(),
      bgColor: "orange",
      route: "/quicklinkgroups",
    },
  ];

  const handleCardClick = (route: string) => {
    navigate(route); // Navigate to the clicked card's route
  };

  return (
    <Box className="dashboard-container">
      <Box className="dashboard-header">
        <Heading size="lg" color="gray.700">
          Welcome, {name}
        </Heading>
        <Text fontSize="lg" color="gray.600" mt="4">
          We're glad to have you back. Here's a summary of your dashboard.
        </Text>
      </Box>

      {/* Grid layout for data cards */}
      <Grid className="dashboard-grid">
        {mockData.map((item, index) => (
          <GridItem
            key={index}
            className={`dashboard-card ${item.bgColor}`}
            onClick={() => handleCardClick(item.route)} // Add click handler to navigate
          >
            <Heading size="md">{item.value}</Heading>
            <Text mt="2">{item.title}</Text>
            <Text color="#fff">Active : {item.activeValue}</Text>
          </GridItem>
        ))}
      </Grid>
    </Box>
  );
};

export default Dashboard;
